import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ExclusionListService } from './exclusion-list.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-exclusion-list',
    templateUrl: './exclusion-list.component.html',
    styleUrls: ['./exclusion-list.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExclusionListComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: any = {};
  filterParams: any = {};
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  gridData: any = {};

  @Input('grid-data')
  set _gridData(gridData: string){
    try{
      this.gridData = safeJsonInput<any>(gridData, null, 'exclusion-list.parse1');
      this.tryInit();
    }catch(e){
      // Grid data input not yet stable
    }
  }

  modalTitle = 'LE Overrides Setup';
  isEdit = 'INSERT';
  showEntityLoader = true;
  isSaving = false;
  minimise = false;
  isSaveClicked = false;

  startTaxYear: number | null = null;
  endTaxYear: number | null = null;
  showStartYearPicker: boolean = false;
  showEndYearPicker: boolean = false;

  consolGroups: any[] = [];
  exclusionEntity: any = {
    LEGAL_ENTITY_ID: '',
    FORM_CATEGORY: '851',
    START_TAX_YEAR: '',
    END_TAX_YEAR: '',
    STATUS: 'EXCLUDE',
    STATUS_COMMENT: '',
    FILING_KEY: ''
  };
  exclusionEntity_bckup: any = {};

  validationErrors = {
    LEGAL_ENTITY_ID_NOT_VALID: false,
    FORM_CATEGORY_NOT_VALID: false,
    FILING_KEY_NOT_VALID: false,
    START_TAX_YEAR_NOT_VALID: false,
    END_TAX_YEAR_NOT_VALID: false,
    STATUS_NOT_VALID: false
  };

  dateOptions: any = {
    dateDisabled: false,
    formatYear: 'yyyy',
    startingDay: 1,
    minMode: 'year',
    dateFormat: 'yyyy'
  };

  scenarioCode: string = 'RAF';
  isIssueKeyEnabled: string = 'N';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private exclusionListService: ExclusionListService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = gc;
    this.GENERAL_CONFIG = { ...(header.generalConfigData || {}), base_url: gc.base_url || gc.api || '' };
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  private tryInit(): void {
    const baseOk = !!this.resolveApiBase();
    if (!baseOk || this.gridData === undefined || !this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.init();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      if (this.gridData !== undefined && this.filterParams && Object.keys(this.filterParams).length > 0) {
        this.setupComponentData();
        this.loadConsolGroups();
      }
    } catch (e) {
      console.error('Error in ExclusionList init:', e);
      this.showEntityLoader = false;
    }
  }

  private setupComponentData() {
    // Determine if INSERT or UPDATE mode
    this.isEdit = (this.gridData.rowData && this.gridData.rowData.LEGAL_ENTITY_ID) ? 'UPDATE' : 'INSERT';

    // Get scenario code from filter params
    if (this.filterParams && this.filterParams.scenario_code) {
      this.scenarioCode = this.filterParams.scenario_code;
    }

    // Set issue key enabled flag
    if (this.scenarioCode === 'RAF') {
      this.isIssueKeyEnabled = 'N';
    } else {
      this.isIssueKeyEnabled = 'Y';
    }

    // Setup entity data
    if (this.isEdit === 'INSERT') {
      // Set default values for INSERT
      const currentYear = this.filterParams.tax_year ? parseInt(this.filterParams.tax_year) : new Date().getFullYear();

      this.startTaxYear = currentYear - 1;
      this.endTaxYear = 9999;

      this.exclusionEntity_bckup = {
        LEGAL_ENTITY_ID: '',
        FORM_CATEGORY: '851',
        START_TAX_YEAR: this.startTaxYear.toString(),
        END_TAX_YEAR: this.endTaxYear.toString(),
        STATUS: 'EXCLUDE',
        STATUS_COMMENT: '',
        FILING_KEY: this.filterParams.filing_key || ''
      };
    } else {
      // Use existing data for UPDATE
      this.exclusionEntity_bckup = _.cloneDeep(this.gridData.rowData);

      // Extract years for year picker
      if (this.exclusionEntity_bckup.START_TAX_YEAR) {
        const yearStr = this.extractYear(this.exclusionEntity_bckup.START_TAX_YEAR);
        this.startTaxYear = parseInt(yearStr);
        this.exclusionEntity_bckup.START_TAX_YEAR = yearStr;
      }
      if (this.exclusionEntity_bckup.END_TAX_YEAR) {
        const yearStr = this.extractYear(this.exclusionEntity_bckup.END_TAX_YEAR);
        this.endTaxYear = parseInt(yearStr);
        this.exclusionEntity_bckup.END_TAX_YEAR = yearStr;
      }

      // Set filing key if not present
      if (!this.exclusionEntity_bckup.FILING_KEY && this.filterParams.filing_key) {
        this.exclusionEntity_bckup.FILING_KEY = this.filterParams.filing_key;
      }
    }

    // Create working copy
    this.exclusionEntity = _.cloneDeep(this.exclusionEntity_bckup);
  }

  private extractYear(dateValue: any): string {
    // If it's already a string year, return it
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}$/)) {
      return dateValue;
    }

    // If it's a Date object
    if (Object.prototype.toString.call(dateValue) === "[object Date]") {
      return dateValue.getFullYear().toString();
    }

    // If it's a date string or number, convert to Date and extract year
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.getFullYear().toString();
    }

    // Fallback - try to parse as year
    return dateValue.toString();
  }

  loadConsolGroups() {
    this.showEntityLoader = true;

    this.exclusionListService.loadConsolGroups(
      this.resolveApiBase(),
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.callSuccess === '1' && response.jsonObject) {
          this.consolGroups = response.jsonObject;
        } else {
          console.warn('Unexpected response format for consol groups:', response);
          this.consolGroups = [];
        }
        this.showEntityLoader = false;
      },
      (error: any) => {
        console.error('Error loading consol groups:', error);
        this.gtwSnackbar.openSnackBar('Error loading Filing Key options', 'gtw-snackbar--red', true, 4);
        this.showEntityLoader = false;
      }
    );
  }

  upper(value: string) {
    if (value) {
      this.exclusionEntity.LEGAL_ENTITY_ID = value.toUpperCase();
    }
  }

  isValid(): boolean {
    // Reset all validation errors
    this.validationErrors.LEGAL_ENTITY_ID_NOT_VALID = false;
    this.validationErrors.FORM_CATEGORY_NOT_VALID = false;
    this.validationErrors.FILING_KEY_NOT_VALID = false;
    this.validationErrors.START_TAX_YEAR_NOT_VALID = false;
    this.validationErrors.END_TAX_YEAR_NOT_VALID = false;
    this.validationErrors.STATUS_NOT_VALID = false;

    let valid = true;

    if (!this.exclusionEntity.LEGAL_ENTITY_ID || this.exclusionEntity.LEGAL_ENTITY_ID.trim() === '') {
      this.validationErrors.LEGAL_ENTITY_ID_NOT_VALID = true;
      valid = false;
    }

    if (!this.exclusionEntity.START_TAX_YEAR) {
      this.validationErrors.START_TAX_YEAR_NOT_VALID = true;
      valid = false;
    }

    if (!this.exclusionEntity.END_TAX_YEAR) {
      this.validationErrors.END_TAX_YEAR_NOT_VALID = true;
      valid = false;
    }

    return valid;
  }

  save() {
    const baseUrl = this.resolveApiBase();
    if (this.isSaving || !baseUrl) return;

    if (!this.isValid()) {
      this.gtwSnackbar.openSnackBar('Please fill all the required fields', 'gtw-snackbar--yellow', true, 4);
      return;
    }

    this.isSaving = true;
    this.isSaveClicked = true;

    // Prepare save data
    const jsonObj = _.cloneDeep(this.exclusionEntity);
    jsonObj.ACTION = this.isEdit;

    // Dates are already in year string format, no conversion needed
    // Just ensure they are strings
    jsonObj.START_TAX_YEAR = jsonObj.START_TAX_YEAR.toString();
    jsonObj.END_TAX_YEAR = jsonObj.END_TAX_YEAR.toString();

    const jsonSettings = {
      tax_year: this.filterParams.tax_year
    };

    // Pass rowData for proper checkLockedbj construction
    const rowData = this.gridData?.rowData || null;

    this.exclusionListService.saveExclusionList(
      this.resolveApiBase(),
      jsonObj,
      jsonSettings,
      this.globalService,
      this.filterParams,
      this.scenarioCode,
      this.isIssueKeyEnabled,
      rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaving = false;
        this.isSaveClicked = false;

        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this.gtwSnackbar.openSnackBar('LE Override Updated Successfully', 'gtw-snackbar--green', true, 4);

          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbar.openSnackBar(response.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red', true, 4);
        }
      },
      (error: any) => {
        this.isSaving = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
      }
    );
  }

  reset() {
    this.exclusionEntity = _.cloneDeep(this.exclusionEntity_bckup);

    // Reset the year picker values
    if (this.exclusionEntity_bckup.START_TAX_YEAR) {
      this.startTaxYear = parseInt(this.exclusionEntity_bckup.START_TAX_YEAR);
    }
    if (this.exclusionEntity_bckup.END_TAX_YEAR) {
      this.endTaxYear = parseInt(this.exclusionEntity_bckup.END_TAX_YEAR);
    }

    // Reset validation errors
    this.validationErrors.LEGAL_ENTITY_ID_NOT_VALID = false;
    this.validationErrors.FORM_CATEGORY_NOT_VALID = false;
    this.validationErrors.FILING_KEY_NOT_VALID = false;
    this.validationErrors.START_TAX_YEAR_NOT_VALID = false;
    this.validationErrors.END_TAX_YEAR_NOT_VALID = false;
    this.validationErrors.STATUS_NOT_VALID = false;
  }

  cancel() {
    // Emit cancel event to close modal
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.VALUE || index : index;
  }

  // Year Picker methods
  openStartYearPicker(): void {
    // Close the End Year picker if it's open
    if (this.showEndYearPicker) {
      this.showEndYearPicker = false;
    }
    this.showStartYearPicker = true;
  }

  closeStartYearPicker(): void {
    this.showStartYearPicker = false;
  }

  onStartYearSelected(year: number | null): void {
    if (year === null) {
      // Clear the year
      this.startTaxYear = null;
      this.exclusionEntity.START_TAX_YEAR = '';
    } else {
      this.startTaxYear = year;
      this.exclusionEntity.START_TAX_YEAR = year.toString();
    }
    this.showStartYearPicker = false;
  }

  openEndYearPicker(): void {
    // Close the Start Year picker if it's open
    if (this.showStartYearPicker) {
      this.showStartYearPicker = false;
    }
    this.showEndYearPicker = true;
  }

  closeEndYearPicker(): void {
    this.showEndYearPicker = false;
  }

  onEndYearSelected(year: number | null): void {
    if (year === null) {
      // Clear the year
      this.endTaxYear = null;
      this.exclusionEntity.END_TAX_YEAR = '';
    } else {
      this.endTaxYear = year;
      this.exclusionEntity.END_TAX_YEAR = year.toString();
    }
    this.showEndYearPicker = false;
  }
}

